/** Cell status hierarchy — Detail-Flow UX (U-phase).
 *
 * One primary signal per cell. Hierarchy (highest priority first):
 *   korrigieren  — klasse 5b OR confidence < thresholds.red → must fix
 *   pruefen      — klasse 1/2 with confidence < thresholds.green → verify
 *   autoFixed    — klasse 2 with conf ≥ thresholds.green → ⚙ icon
 *   ignoriert    — klasse 5a → marginalia, dimmed
 *   ok           — klasse 1/3/4 with high confidence → no signal (green/default)
 *
 * Doctrine: never show TWO color-signals on the same cell. The bucketed
 * confidence-bg from `confidence.ts` is reduced to 10% opacity (U9) so
 * cellStatus is the dominant primary signal.
 *
 * Thresholds are admin-tunable per client (Track B/C, 2026-05-11). They
 * MUST be passed in by callers — the hardcoded constant is gone. Use
 * `useThresholds()` from `ClientSettingsContext` in React code.
 */

import type { Klasse } from "./validator";

export type CellStatus = "korrigieren" | "pruefen" | "autoFixed" | "ignoriert" | "ok";

export interface CellThresholds {
  red: number;   // confidence below this → korrigieren (e.g. 0.4)
  green: number; // confidence above this → ok / autoFixed (e.g. 0.85)
}

/** Coded fallback identical to backend `DEFAULTS` (hub/src/repos/app_settings.ts).
 *  Used by tests and any component called before the Context has loaded. */
export const DEFAULT_THRESHOLDS: CellThresholds = { red: 0.4, green: 0.85 };

export const STATUS_PRIORITY: Record<CellStatus, number> = {
  korrigieren: 0,
  pruefen: 1,
  autoFixed: 2,
  ignoriert: 3,
  ok: 4,
};

export const STATUS_ICONS: Record<CellStatus, string> = {
  korrigieren: "🔴",
  pruefen: "🟡",
  autoFixed: "⚙",
  ignoriert: "⚪",
  ok: "🟢",
};

export const STATUS_LABELS: Record<CellStatus, string> = {
  korrigieren: "korrigieren",
  pruefen: "prüfen",
  autoFixed: "auto-repariert",
  ignoriert: "ignoriert",
  ok: "ok",
};

export const STATUS_CLASS: Record<CellStatus, string> = {
  korrigieren: "cs-korrigieren",
  pruefen: "cs-pruefen",
  autoFixed: "cs-auto",
  ignoriert: "cs-ignoriert",
  ok: "cs-ok",
};

/** Pure: classify a cell's display status from validator klasse + confidence + value.
 *
 * `thresholds` controls the konfidenz-grenzen für rot/grün und ist
 * admin-tunable (Track B/C). Wenn nicht übergeben werden die coded defaults
 * (0.4 / 0.85) genutzt — wichtig damit Tests + non-React callers nicht
 * silent fail.
 *
 * After L0 (schema-strict validator, 2026-05-06), klasse-5b cells have
 * `value=null` (raw OCR preserved only in audit.raw_text). klasse-5b
 * ALWAYS means "OCR couldn't read it / reviewer must fill in" — the cell
 * renders as empty in the UI but counts toward the attention budget.
 *
 * Empty-because-no-data is klasse-4 (the validator's normal empty path).
 */
export function cellStatus(
  klasse: Klasse | undefined,
  confidence: number | undefined,
  value: string | undefined,
  thresholds: CellThresholds = DEFAULT_THRESHOLDS,
): CellStatus {
  const conf = typeof confidence === "number" ? confidence : 1;

  // Hard schema-violation always wins
  if (klasse === "5b") return "korrigieren";

  // 5a — marginalia silent (e.g. printed Sonntag/Feiertag tokens)
  if (klasse === "5a") return "ignoriert";

  // Very low confidence on a populated value → korrigieren (Track A
  // disagreement signal lands here when paddle is confident-disagree).
  if (klasse !== "4" && conf < thresholds.red && value && value.length > 0) {
    return "korrigieren";
  }

  // Mid-confidence on a "valid" cell → pruefen
  if ((klasse === "1" || klasse === "2") && conf < thresholds.green) {
    return "pruefen";
  }

  // Auto-repair bubbles below pruefen
  if (klasse === "2") return "autoFixed";

  // klasse 1, 3, 4 → ok
  return "ok";
}

/** Comparator for sorting cells by attention-priority (asc = most-attention-first). */
export function cellStatusCompare(a: CellStatus, b: CellStatus): number {
  return STATUS_PRIORITY[a] - STATUS_PRIORITY[b];
}

/** Aggregate counts across a batch of cells — for AttentionCounter banner +
 *  ProgressTracker bottom-bar. */
export function statusBreakdown(statuses: CellStatus[]): Record<CellStatus, number> {
  const out: Record<CellStatus, number> = {
    korrigieren: 0, pruefen: 0, autoFixed: 0, ignoriert: 0, ok: 0,
  };
  for (const s of statuses) out[s]++;
  return out;
}

/** True when the user should focus on this cell (skip via Tab when false). */
export function needsAttention(status: CellStatus): boolean {
  return status === "korrigieren" || status === "pruefen";
}
